import { useEffect, useRef, useState } from "react";

function initDataLayer() {
  if (typeof window === "undefined") {
    return;
  }

  if (window.__travelguideOoiTracking) {
    return window.__travelguideOoiTracking;
  }

  const debounceDuration = 300;
  let debounceTimer: NodeJS.Timeout | null = null;
  let ids: Array<Travelguide.ID> = [];

  function track(id: Travelguide.ID) {
    ids.push(id);

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(() => {
      if (!("oacrr" in window)) {
        return;
      }
      try {
        window.oacrr.track("teaser", ids);
      } catch (e) {
        console.error("Could not track:", e);
        return;
      }
      ids = [];
    }, debounceDuration);
  }

  const array: Array<Travelguide.ID> = [];
  const observedArray = new Proxy(array, {
    set(target, property, value) {
      if (!isNaN(Number(property))) {
        if (target.includes(value)) {
          return true;
        }
        track(value);
      }
      target[property as never] = value;
      return true;
    },
  });

  return (window.__travelguideOoiTracking = observedArray);
}

export function useTracking(trackingId: Travelguide.ID, autoTrack = false) {
  const [inView, setInView] = useState(false);
  const autoTrackingRef = useRef<HTMLDivElement>(null);

  const dataLayer = initDataLayer();

  useEffect(() => {
    if (!autoTrack || !autoTrackingRef.current) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setInView(entry.isIntersecting);
        });
      },
      {
        root: null,
        threshold: 0.5,
      }
    );

    observer.observe(autoTrackingRef.current);

    return () => {
      if (!autoTrackingRef.current) {
        return;
      }
      observer.unobserve(autoTrackingRef.current);
    };
  }, [autoTrackingRef]);

  useEffect(() => {
    if (autoTrack && !inView) {
      return;
    }
    dataLayer?.push(trackingId);
  }, [inView, trackingId]);

  return { autoTrackingRef, dataLayer };
}
